import React, { memo, useCallback, useMemo } from "react";
import {
	ProposalBuilderProvider,
	withProposalBuilderProvider
} from "@/ProposalBuilder/store";
import {
	DocumentDetailsRequest,
	EditDocumentRequest,
	MergeTagVariable,
	AssignUserToSignatureBlock,
	ClientContactBulkUpdateRequest,
	DocumentPayment,
	Assessment,
	Qbr,
	PricingPlanNames,
	UserRole,
	AllRolePermissions,
	ModifyCategoryRequest,
	UpdateBlockListSequenceRequest,
	EditorType,
	Template,
	VendorTemplateDetail
} from "@/Contracts";
import {
	Document,
	UpdateMultipleBlockListSequenceRequest
} from "@/Contracts/Documents";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";
import { usePaymentActions } from "@/Hooks/Documents/Payments/usePaymentActions";
import DocumentsActionCreators, {
	DocumentsActionTypes
} from "@/Actions/Documents";
import TemplatesActionCreators, {
	TemplatesActionTypes
} from "@/Actions/Templates";
import AssessmentsActionCreators from "@/Actions/Assessments";
import QbrActionCreators from "@/Actions/QBR";
import UsersActionCreators from "@/Actions/User";
import ClientsActionCreators from "@/Actions/Clients";
import useDispatch from "../../../Components/Utils/useDispatch";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import { useFeatureFlag } from "@/Hooks/useFeature";
import useDatePattern from "@/Hooks/useDatePattern";
import usePermissions from "@/Hooks/usePermissions";
import { Modules, ModulePermission } from "@/Contracts/Permission";
import useEntityData from "@/Hooks/useEntityData";
import { useAllowZeroOrNegativeQuantity } from "@/Hooks/Integrations/useAllowZeroOrNegativeQuantity";
import SignatureBlockTimestampManager from "@/CKEditorComponent/Editor/plugins/SignatureBlockTimestampManager";

import AddClientContactDrawer from "@pages/Drawers/Clients/ClientContact/Add";
import CloneProductToQuoteBlockDrawer from "@pages/Drawers/Documents/CloneProductToQuoteDrawer";
import { AddProductToQuoteBlockDrawer } from "@/Components/DocumentsPage/Drawers/DocumentProductDrawers/AddProductToQuoteBlockDrawer";
import { DocumentCurrencyProvider } from "@/ProposalBuilder/contexts/DocumentCurrencyContext";
import AddProductDrawer from "@pages/Drawers/Products/Product";

import "@/ProposalBuilder/styles.less";
import useDownload from "@/Hooks/Documents/DocumentActions/useDownload";
import { useNewQuoteBlockDesign } from "@/Hooks";

const ProposalBuilderWrapper: React.FC = ({ children }) => {
	const {
		currentActiveDocument,
		currentActiveTemplate,
		currentActiveAssessment,
		userCompany,
		userData,
		taxRegionsList,
		taxCategoriesList,
		companyUserList,
		activeDocumentPanel,
		activeTemplatePanel,
		previewPaymentSummary,
		previewPaymentTerms,
		addClientContactDrawerOpen,
		isTemplateLoading,
		isDocumentLoading,
		subscriptions,
		subscriptionDetails,
		billingInfo,
		isFetchingPDF
	} = useSelector((state: GlobalState) => {
		return {
			currentActiveDocument: state.documents.currentActiveDocument,
			currentActiveTemplate: state.templates.currentActiveTemplate,
			currentActiveAssessment: state.assessments.currentActiveAssessment,
			userCompany: state.user.userCompany,
			userData: state.user.userData,
			taxRegionsList: state.user.taxRegionsList,
			taxCategoriesList: state.user.taxCategoriesList,
			companyUserList: state.user.companyUserList,
			activeDocumentPanel: state.documents.activeDocumentPanel,
			activeTemplatePanel: state.templates.activeTemplatePanel,
			previewPaymentSummary: state.documents.previewPaymentSummary,
			previewPaymentTerms: state.documents.previewPaymentTerms,
			addClientContactDrawerOpen:
				state.clients.addClientContactDrawerOpen,
			isTemplateLoading: state.templates.isLoading,
			isDocumentLoading: state.documents.isLoading,
			subscriptions: state.user.subscriptions,
			subscriptionDetails: state.user.subscriptionDetails,
			billingInfo: state.user.billingInfo,
			isFetchingPDF: state.settings.isFetchingPDF
		};
	});
	const dispatch = useDispatch();
	const isQuoteBlockRevamped =
		!!currentActiveDocument?.is_quote_block_revamped;
	const { fetchDocumentPdfUrl } = useDownload();
	const getDocumentDetailsFromClient = useCallback(
		async (documentDetailsRequest: DocumentDetailsRequest) => {
			await dispatch(
				DocumentsActionCreators.getDocumentDetailsFromClient(
					documentDetailsRequest
				)
			);
		},
		[]
	);
	const getDocumentDetails = useCallback(
		async (documentDetailsRequest: DocumentDetailsRequest) => {
			await dispatch(
				DocumentsActionCreators.getDocumentDetails(
					documentDetailsRequest
				)
			);
		},
		[]
	);
	const showSignFlowConfirmationModal = useCallback(() => {
		dispatch(DocumentsActionCreators.showSignFlowConfirmationModal());
	}, []);
	const updateCurrentDocument = useCallback(
		async (updateCurrentDocumentRequest: EditDocumentRequest) => {
			await dispatch(
				DocumentsActionCreators.updateCurrentDocument(
					updateCurrentDocumentRequest
				)
			);
		},
		[]
	);
	const updateDocumentPaymentSettings = useCallback(
		async (
			documentId: string,
			documentPaymentUpdateRequest: DocumentPayment
		) => {
			await dispatch(
				DocumentsActionCreators.updateDocumentPaymentSettings(
					documentId,
					documentPaymentUpdateRequest
				)
			);
		},
		[]
	);

	const assignSignatureBlockToRecipient = useCallback(
		async ({
			blockId,
			signerId
		}: {
			blockId: string;
			signerId?: string;
		}) => {
			try {
				const result = (
					await dispatch(
						DocumentsActionCreators.assignSignatureBlockToRecipient(
							{
								blockId,
								signerId
							}
						)
					)
				)?.updateCurrentDocument;

				// Update timestamp after successful assignment using singleton
				const timestampManager =
					SignatureBlockTimestampManager.getInstance();
				timestampManager.updateTimestamp(blockId);

				return result;
			} catch (error) {
				console.error(error);
				throw error;
			}
		},
		[]
	);
	const setDocumentSidePanel = useCallback((panelTab: any) => {
		dispatch(DocumentsActionCreators.setSidePanel(panelTab));
	}, []);
	const updateParentCurrentActiveDocument = useCallback(
		(document: Document) => {
			dispatch({
				type: DocumentsActionTypes.DOCUMENT_DETAILS_SUCCESSFUL,
				currentActiveDocument: document
			});
		},
		[]
	);
	const updateParentCurrentActiveTemplate = useCallback(
		(template: Template | VendorTemplateDetail) => {
			dispatch({
				type: TemplatesActionTypes.TEMPLATE_DETAILS_SUCCESSFUL,
				currentActiveTemplate: template
			});
		},
		[]
	);
	const updateSignatureAssignee = useCallback(
		async (userDetails: AssignUserToSignatureBlock) => {
			await dispatch(
				TemplatesActionCreators.updateSignatureAssignee(userDetails)
			);

			// Update timestamp after successful assignment using singleton
			if (userDetails.block_id) {
				const timestampManager =
					SignatureBlockTimestampManager.getInstance();
				timestampManager.updateTimestamp(userDetails.block_id);
			}
		},
		[]
	);
	const setTemplateSidePanel = useCallback((panelTab: any) => {
		dispatch(TemplatesActionCreators.setSidePanel(panelTab));
	}, []);
	const editAssessmentDetails = useCallback(
		async (assessment: Assessment) => {
			await dispatch(
				AssessmentsActionCreators.editAssessmentDetails(assessment)
			);
		},
		[]
	);
	const editQbrDetails = useCallback(async (qbr: Qbr) => {
		await dispatch(QbrActionCreators.editQbrDetails(qbr));
	}, []);
	const showLibraryLoader = useCallback(() => {
		dispatch(UsersActionCreators.showLibraryLoader());
	}, []);
	const saveBlockToLibrary = useCallback(
		async (createLibraryItemRequest: any) => {
			await dispatch(
				UsersActionCreators.saveBlockToLibrary(createLibraryItemRequest)
			);
		},
		[]
	);
	const fetchLibrary = useCallback(async (fetchLibraryRequest: any) => {
		await dispatch(UsersActionCreators.fetchLibrary(fetchLibraryRequest));
	}, []);
	const hideLibraryLoader = useCallback(() => {
		dispatch(UsersActionCreators.hideLibraryLoader());
	}, []);
	const addClientContactDrawerOpened = useCallback(() => {
		dispatch(ClientsActionCreators.addClientContactDrawerOpened());
	}, []);
	const addClientContactDrawerClosed = useCallback(() => {
		dispatch(ClientsActionCreators.addClientContactDrawerClosed());
	}, []);
	const bulkUpdateClientContactEmail = useCallback(
		async (bulkUpdateRequest: ClientContactBulkUpdateRequest) => {
			return await dispatch(
				ClientsActionCreators.bulkUpdateClientContactEmail(
					bulkUpdateRequest
				)
			);
		},
		[]
	);
	const hasAccess = useFeatureAccess(
		PricingPlanNames.LAUNCH,
		UserRole.UserRole
	);
	const hasSignatureBlockReadPermission = useFeatureAccess(
		PricingPlanNames.LAUNCH,
		UserRole.UserRole,
		[AllRolePermissions.SIGNATURE_BLOCK_READ]
	);
	const showZPricingPlanErrorTooltip = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		[AllRolePermissions.SIGNATURE_BLOCK_READ],
		!hasAccess
	);
	const paymentSummaryFeatureFlag = useFeatureFlag("payment_summary_block");
	const isNewQuoteBlockDesign = useNewQuoteBlockDesign(isQuoteBlockRevamped);
	const datePattern = useDatePattern();

	const clientAddPermissions = usePermissions(
		Modules.CLIENT_COMPANY,
		ModulePermission.CREATE_EDIT_DELETE_OWNED
	);
	const hasClientAddPermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		clientAddPermissions
	);
	const hasProductAddPermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		[AllRolePermissions.QUOTE_BLOCK_ADD]
	);
	const hasProductEditPermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		[
			AllRolePermissions.QUOTE_BLOCK_ADD,
			AllRolePermissions.QUOTE_BLOCK_UPDATE
		]
	);
	const hasProductCreateAndAddPermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		[
			AllRolePermissions.ITEM_READ,
			AllRolePermissions.ITEM_CREATE,
			AllRolePermissions.QUOTE_BLOCK_ADD
		]
	);
	const updateDocumentMergeTags = useCallback(
		async (mergeTagVariables: MergeTagVariable[]) => {
			return dispatch(
				DocumentsActionCreators.updateMergeTags(mergeTagVariables)
			);
		},
		[]
	);
	const updateTemplateMergeTags = useCallback(
		async (mergeTagVariables: MergeTagVariable[]) => {
			return dispatch(
				TemplatesActionCreators.updateMergeTags(mergeTagVariables)
			);
		},
		[]
	);
	const resetAssessmentRiskScore = useCallback(
		async (assessmentId: string) => {
			dispatch(AssessmentsActionCreators.resetRiskScore(assessmentId));
		},
		[]
	);
	const resetQbrRiskScore = useCallback(async (qbrId: string) => {
		dispatch(QbrActionCreators.resetRiskScore(qbrId));
	}, []);

	const updateAssessmentCategory = useCallback(
		async (modifyCategoryRequest: ModifyCategoryRequest) => {
			await dispatch(
				AssessmentsActionCreators.updateCategory(modifyCategoryRequest)
			);
		},
		[]
	);
	const updateQbrCategory = useCallback(
		async (modifyCategoryRequest: ModifyCategoryRequest) => {
			await dispatch(
				QbrActionCreators.updateCategory(modifyCategoryRequest)
			);
		},
		[]
	);

	const {
		currentActiveEntity,
		documentType,
		legendLabels,
		manualIssuesList,
		ms365AutoAssmtIssuesList,
		categoryNamesList
	} = useEntityData();
	const { negativeQuantity, zeroQuantity } = useAllowZeroOrNegativeQuantity();

	const { removePaymentSummaryBlockFromDocument } = usePaymentActions();

	const addBlocks = useCallback(
		async (
			{ draggableId, index }: UpdateBlockListSequenceRequest,
			editorType: EditorType
		) => {
			if (editorType === EditorType.GROW_DOCUMENT)
				await dispatch(
					DocumentsActionCreators.addBlockToRevision({
						draggableId,
						index,
						isNewQuoteBlockDesign
					})
				);
			else
				await dispatch(
					TemplatesActionCreators.addBlockToList({
						draggableId,
						index
					})
				);
		},
		[]
	);

	const linkBlockList = useCallback(
		async (
			{ blockList, index }: UpdateMultipleBlockListSequenceRequest,
			editorType: EditorType
		) => {
			if (editorType === EditorType.GROW_DOCUMENT)
				await dispatch(
					DocumentsActionCreators.linkBlockListToRevision({
						blockList,
						index
					})
				);
			else
				await dispatch(
					TemplatesActionCreators.linkBlockListToTemplate({
						blockList,
						index
					})
				);
		},
		[]
	);

	const deleteBlocks = useCallback(
		async (blockIds: string[], editorType: EditorType) => {
			if (editorType === EditorType.GROW_DOCUMENT)
				await dispatch(
					DocumentsActionCreators.deleteBlockFromRevision({
						blockIds: blockIds
					})
				);
			else
				await dispatch(
					TemplatesActionCreators.deleteBlockFromRevision({
						blockIds: blockIds
					})
				);
		},
		[]
	);

	const hideReceiverMergeTagFeatureFlag = useFeatureFlag(
		"receiver_merge_tags"
	);
	const movePreferencesGlobal = useFeatureFlag("move_preferences_global");

	const document = useMemo(
		() => ({
			isDocumentLoading,
			activeDocumentPanel,
			previewPaymentSummary,
			previewPaymentTerms,
			getDocumentDetailsFromClient,
			getDocumentDetails,
			showSignFlowConfirmationModal,
			updateCurrentDocument,
			updateDocumentPaymentSettings,
			assignSignatureBlockToRecipient,
			setDocumentSidePanel,
			updateParentCurrentActiveDocument
		}),
		[
			isDocumentLoading,
			activeDocumentPanel,
			previewPaymentSummary,
			previewPaymentTerms
		]
	);

	const template = useMemo(
		() => ({
			isTemplateLoading,
			activeTemplatePanel,
			updateSignatureAssignee,
			setTemplateSidePanel,
			updateParentCurrentActiveTemplate
		}),
		[isTemplateLoading, activeTemplatePanel]
	);

	const mergeTags = useMemo(
		() => ({
			updateDocumentMergeTags,
			updateTemplateMergeTags
		}),
		[]
	);

	const assessment = useMemo(
		() => ({
			currentActiveAssessment,
			resetRiskScore: resetAssessmentRiskScore,
			editDetails: editAssessmentDetails,
			updateCategory: updateAssessmentCategory
		}),
		[currentActiveAssessment]
	);

	const qbr = useMemo(
		() => ({
			resetRiskScore: resetQbrRiskScore,
			editDetails: editQbrDetails,
			updateCategory: updateQbrCategory
		}),
		[]
	);

	const library = useMemo(
		() => ({
			showLibraryLoader,
			saveBlockToLibrary,
			fetchLibrary,
			hideLibraryLoader
		}),
		[]
	);

	const drawers = useMemo(
		() => ({
			AddClientContactDrawer,
			CloneProductToQuoteBlockDrawer,
			AddProductToQuoteBlockDrawer,
			AddProductDrawer
		}),
		[]
	);

	const permissions = useMemo(
		() => ({
			hasAccess,
			hasSignatureBlockReadPermission,
			showZPricingPlanErrorTooltip,
			hasClientAddPermission,
			hasProductAddPermission,
			hasProductEditPermission,
			hasProductCreateAndAddPermission
		}),
		[
			hasAccess,
			hasSignatureBlockReadPermission,
			showZPricingPlanErrorTooltip,
			hasClientAddPermission,
			hasProductAddPermission,
			hasProductEditPermission,
			hasProductCreateAndAddPermission
		]
	);

	const client = useMemo(
		() => ({
			addClientContactDrawerOpen,
			addClientContactDrawerOpened,
			addClientContactDrawerClosed,
			bulkUpdateClientContactEmail
		}),
		[addClientContactDrawerOpen]
	);

	const featureFlag = useMemo(
		() => ({
			paymentSummaryFeatureFlag,
			hideReceiverMergeTagFeatureFlag,
			movePreferencesGlobal
		}),
		[
			paymentSummaryFeatureFlag,
			hideReceiverMergeTagFeatureFlag,
			movePreferencesGlobal
		]
	);

	const user = useMemo(
		() => ({
			userCompany,
			userData,
			companyUserList,
			datePattern,
			subscriptions,
			subscriptionDetails,
			billingInfo
		}),
		[
			userCompany,
			userData,
			companyUserList,
			datePattern,
			subscriptions,
			subscriptionDetails,
			billingInfo
		]
	);

	const entityData = useMemo(
		() => ({
			currentActiveEntity,
			documentType,
			legendLabels,
			manualIssuesList,
			ms365AutoAssmtIssuesList,
			categoryNamesList
		}),
		[
			currentActiveEntity,
			documentType,
			legendLabels,
			manualIssuesList,
			ms365AutoAssmtIssuesList,
			categoryNamesList
		]
	);

	const allowZeroOrNegativeQuantity = useMemo(
		() => ({
			negativeQuantity,
			zeroQuantity
		}),
		[negativeQuantity, zeroQuantity]
	);

	const parentState = useMemo(
		() => ({
			currentActiveDocument,
			currentActiveTemplate,
			taxCategoriesList,
			taxRegionsList
		}),
		[
			currentActiveDocument,
			currentActiveTemplate,
			taxCategoriesList,
			taxRegionsList
		]
	);

	const pdf = useMemo(
		() => ({
			fetchDocumentPdfUrl,
			isFetchingPDF
		}),
		[fetchDocumentPdfUrl, isFetchingPDF]
	);

	const currencyConfig = useMemo(
		() => currentActiveDocument?.currency_config ?? null,
		[currentActiveDocument?.currency_config]
	);

	return (
		<DocumentCurrencyProvider config={currencyConfig}>
			<ProposalBuilderProvider
				document={document}
				template={template}
				mergeTags={mergeTags}
				assessment={assessment}
				qbr={qbr}
				library={library}
				drawers={drawers}
				permissions={permissions}
				client={client}
				featureFlag={featureFlag}
				user={user}
				removePaymentSummaryBlockFromDocument={
					removePaymentSummaryBlockFromDocument
				}
				addBlocks={addBlocks}
				linkBlockList={linkBlockList}
				deleteBlocks={deleteBlocks}
				entityData={entityData}
				allowZeroOrNegativeQuantity={allowZeroOrNegativeQuantity}
				pdf={pdf}
				parentState={parentState}
			>
				{children}
			</ProposalBuilderProvider>
		</DocumentCurrencyProvider>
	);
};

export default withProposalBuilderProvider(memo(ProposalBuilderWrapper));
